import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COIN_PRICE_CENTS, MIN_COINS, formatEuro, grossFromNet } from "@/lib/salonturbo";

const STAND = "September 2026";

export const Route = createFileRoute("/agb")({
  head: () => ({
    meta: [
      { title: "AGB – SalonTurbo für Friseursalons" },
      {
        name: "description",
        content:
          "Allgemeine Geschäftsbedingungen von SalonTurbo: Leistungen, Coins und Preise, Laufzeit, Haftung. Ausschließlich für Gewerbetreibende, alle Preise netto.",
      },
      { property: "og:title", content: "AGB – SalonTurbo" },
      {
        property: "og:description",
        content:
          "Allgemeine Geschäftsbedingungen von SalonTurbo für Friseursalons: Coins, Boost-Tage, Laufzeit und Haftung.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
  }),
  component: AgbPage,
});

function Section({
  no,
  title,
  children,
}: {
  no: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-medium">
        {no}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function AgbPage() {
  const net = formatEuro(COIN_PRICE_CENTS);
  const gross = formatEuro(grossFromNet(COIN_PRICE_CENTS));

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-4 py-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Stand: {STAND}
        </p>
        <h1 className="mt-3 font-display text-3xl font-normal tracking-tight sm:text-4xl">
          Allgemeine Geschäftsbedingungen
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Diese Bedingungen gelten für die Nutzung von SalonTurbo. Anbieter ist swax Media; die
          vollständigen Anbieterangaben findest du im{" "}
          <a
            href="https://swax-media.de/impressum"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            Impressum
          </a>
          . SalonTurbo richtet sich ausschließlich an Gewerbetreibende. Alle Preise sind
          Nettopreise zzgl. der gesetzlichen Umsatzsteuer.
        </p>

        <Section no={1} title="Geltungsbereich">
          <p>
            Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen dem
            Anbieter und dem Kunden über die Nutzung der Plattform SalonTurbo und der damit
            verbundenen Werbeleistungen.
          </p>
          <p>
            Das Angebot richtet sich ausschließlich an Unternehmer im Sinne des § 14 BGB. Ein
            Vertragsschluss mit Verbrauchern ist ausgeschlossen; ein Widerrufsrecht besteht daher
            nicht.
          </p>
          <p>
            Abweichende oder entgegenstehende Bedingungen des Kunden werden nicht Vertragsinhalt,
            auch wenn der Anbieter ihnen nicht ausdrücklich widerspricht.
          </p>
        </Section>

        <Section no={2} title="Leistungen von SalonTurbo">
          <p>
            SalonTurbo erstellt für den Salon des Kunden eine eigene Salonseite auf einer Domain des
            Anbieters und legt im Google-Ads-Konto des Anbieters eine auf den Salon zugeschnittene
            Suchkampagne an. Der Kunde benötigt kein eigenes Werbekonto.
          </p>
          <p>
            Der Kunde wählt einzelne Kalendertage aus, an denen geworben werden soll („Boost-Tag“).
            Für jeden gebuchten Boost-Tag aktiviert das System die Kampagne zu Tagesbeginn und
            beendet die Auslieferung planmäßig etwa zwei Stunden vor Ende der hinterlegten
            Öffnungszeit.
          </p>
          <p>
            An Tagen ohne Boost läuft keine Werbung. Die Salonseite bleibt online, jedoch ohne den
            Hinweis auf kurzfristig freie Termine.
          </p>
          <p>
            Der Kunde beauftragt und bevollmächtigt den Anbieter, die Anzeigen in seinem Interesse
            zu gestalten, zu schalten, anzupassen und zu beenden. Gestaltung von Anzeigentexten,
            Keywords, Geo-Ausrichtung und Budgetsteuerung liegen beim Anbieter.
          </p>
        </Section>

        <Section no={3} title="Registrierung und Pflichten des Kunden">
          <p>
            Der Kunde stellt sicher, dass die im Onboarding und in den Einstellungen hinterlegten
            Angaben – insbesondere Salonname, Adresse, Telefonnummer, Buchungslink und
            Öffnungszeiten – vollständig, aktuell und richtig sind. Änderungen pflegt der Kunde
            zeitnah selbst ein.
          </p>
          <p>
            Für hochgeladene oder von der eigenen Website übernommene Bilder, Logos und Texte
            versichert der Kunde, dass er über die erforderlichen Nutzungsrechte verfügt und keine
            Rechte Dritter verletzt werden. Der Kunde stellt den Anbieter von Ansprüchen Dritter
            frei, die auf einer Verletzung dieser Pflicht beruhen.
          </p>
          <p>
            Der Kunde stellt sicher, dass er an gebuchten Boost-Tagen während der Öffnungszeiten
            telefonisch erreichbar ist und tatsächlich Kapazitäten anbieten kann. Irreführende
            Angaben – etwa der Hinweis auf freie Termine ohne Kapazität – sind unzulässig.
          </p>
          <p>Zugangsdaten sind vertraulich zu behandeln.</p>
        </Section>

        <Section no={4} title="Coins, Preise und Zahlung">
          <p>
            Die Leistungen werden über ein Prepaid-Guthaben abgerechnet. Ein Coin kostet {net}{" "}
            netto ({gross} inkl. 19 % Umsatzsteuer) und entspricht einem Boost-Tag.
          </p>
          <p>
            Die Mindestabnahme beim ersten Kauf beträgt {MIN_COINS} Coins. Weitere Coins können
            jederzeit in beliebiger Stückzahl nachgekauft werden.
          </p>
          <p>
            Die Zahlung erfolgt im Voraus über den Zahlungsdienstleister Stripe. Der endgültige
            Steuersatz wird im Checkout anhand der Rechnungsadresse ermittelt; eine USt-IdNr. für
            das Reverse-Charge-Verfahren kann dort angegeben werden. Rechnungen werden elektronisch
            bereitgestellt.
          </p>
          <p>
            Beim Buchen eines Boost-Tages wird ein Coin vom Guthaben abgezogen. Ohne ausreichendes
            Guthaben ist keine Buchung möglich.
          </p>
        </Section>

        <Section no={5} title="Guthaben, Erstattung und Storno">
          <p>Gekaufte Coins verfallen nicht und sind zeitlich unbegrenzt einlösbar.</p>
          <p>
            Eine Auszahlung oder Rückzahlung von Guthaben in Geld ist ausgeschlossen. Coins sind
            nicht auf Dritte übertragbar und nicht handelbar.
          </p>
          <p>
            Wurde ein gebuchter Boost-Tag technisch nicht ausgeliefert – etwa weil der Tag ein
            Ruhetag war oder ein Systemfehler die Aktivierung verhindert hat – erfolgt eine
            Erstattung ausschließlich als Gutschrift des betroffenen Coins auf das Guthaben.
          </p>
          <p>
            Ein gebuchter Boost-Tag kann bis zum Vortag storniert werden; der Coin wird dem Guthaben
            wieder zugeschrieben. Am Tag selbst begonnene Boosts sind verbraucht.
          </p>
        </Section>

        <Section no={6} title="Abhängigkeit von Google und kein Erfolgsversprechen">
          <p>
            Die Auslieferung der Anzeigen erfolgt über Google Ads und unterliegt den Richtlinien,
            Prüfprozessen und der Verfügbarkeit von Google. Freigabe, Reichweite, Klickpreise,
            Platzierung und Wettbewerbssituation liegen außerhalb des Einflussbereichs des
            Anbieters.
          </p>
          <p>
            Der Anbieter schuldet die sorgfältige Einrichtung und Steuerung der Kampagnen, nicht
            einen bestimmten Erfolg. Eine bestimmte Anzahl an Anrufen, Terminen, Klicks,
            Impressionen oder Umsätzen wird nicht zugesagt.
          </p>
          <p>
            Wird eine Anzeige oder ein Konto durch Google beanstandet, abgelehnt oder gesperrt,
            bemüht sich der Anbieter um eine zeitnahe Lösung. Betroffene, nicht ausgelieferte
            Boost-Tage werden nach Ziffer 5 als Coin gutgeschrieben.
          </p>
        </Section>

        <Section no={7} title="Laufzeit und Kündigung">
          <p>
            Es besteht kein Abonnement und keine Mindestlaufzeit. Der Kunde kann die Nutzung
            jederzeit ohne Frist beenden und sein Konto löschen lassen.
          </p>
          <p>
            Beendet der Kunde die Nutzung, verfällt ungenutztes Guthaben ohne Anspruch auf Ersatz.
            Kündigt der Anbieter ohne wichtigen, vom Kunden zu vertretenden Grund, wird ungenutztes
            Guthaben zum Nettokaufpreis erstattet.
          </p>
          <p>
            Der Anbieter kann den Zugang aus wichtigem Grund sperren, insbesondere bei
            irreführenden Angaben, Rechtsverletzungen oder Verstößen gegen Google-Richtlinien.
          </p>
        </Section>

        <Section no={8} title="Haftung">
          <p>
            Der Anbieter haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit, bei Verletzung
            von Leben, Körper oder Gesundheit sowie im Umfang einer übernommenen Garantie und nach
            zwingenden gesetzlichen Vorschriften.
          </p>
          <p>
            Bei leicht fahrlässiger Verletzung einer wesentlichen Vertragspflicht ist die Haftung
            auf den vertragstypischen, vorhersehbaren Schaden begrenzt. Im Übrigen ist die Haftung
            für leichte Fahrlässigkeit ausgeschlossen.
          </p>
          <p>
            Die Haftung ist insgesamt auf die Höhe der vom Kunden in den letzten zwölf Monaten vor
            dem schadensauslösenden Ereignis gezahlten Nettoentgelte begrenzt.
          </p>
          <p>
            Die Haftung für entgangenen Gewinn, ausgefallene Werbewirkung sowie für Störungen bei
            Google oder anderen Drittanbietern ist im Rahmen des gesetzlich Zulässigen
            ausgeschlossen.
          </p>
        </Section>

        <Section no={9} title="Verfügbarkeit">
          <p>
            Der Anbieter bemüht sich um eine hohe Verfügbarkeit der Plattform, sagt jedoch keine
            bestimmte Verfügbarkeitsquote zu.
          </p>
          <p>
            Wartungsarbeiten, Weiterentwicklungen und Störungen bei eingesetzten Dienstleistern
            können zu vorübergehenden Einschränkungen führen. Planbare Wartungsfenster werden nach
            Möglichkeit außerhalb üblicher Öffnungszeiten gelegt.
          </p>
        </Section>

        <Section no={10} title="Datenschutz">
          <p>
            Informationen zur Verarbeitung personenbezogener Daten finden sich in der{" "}
            <a
              href="https://swax-media.de/datenschutzerklaerung"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Datenschutzerklärung
            </a>
            .
          </p>
          <p>
            Soweit der Anbieter personenbezogene Daten im Auftrag des Kunden verarbeitet, wird auf
            Anfrage eine Vereinbarung zur Auftragsverarbeitung geschlossen.
          </p>
        </Section>

        <Section no={11} title="Änderungen dieser AGB">
          <p>
            Der Anbieter kann diese AGB mit Wirkung für die Zukunft ändern, insbesondere bei
            Änderungen der Leistungen, der eingesetzten Dienstleister oder der Rechtslage.
          </p>
          <p>
            Änderungen werden dem Kunden mit angemessener Frist per E-Mail angekündigt. Widerspricht
            der Kunde nicht bis zum genannten Zeitpunkt oder nutzt er die Plattform danach weiter,
            gelten die geänderten Bedingungen als angenommen. Im Fall des Widerspruchs kann jede
            Partei den Vertrag beenden.
          </p>
        </Section>

        <Section no={12} title="Schlussbestimmungen">
          <p>Es gilt das Recht der Bundesrepublik Deutschland.</p>
          <p>
            Ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem Vertrag ist der Sitz
            des Anbieters, soweit der Kunde Kaufmann, juristische Person des öffentlichen Rechts
            oder öffentlich-rechtliches Sondervermögen ist.
          </p>
          <p>
            Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der
            übrigen Bestimmungen unberührt.
          </p>
          <p>Stand dieser Bedingungen: {STAND}.</p>
        </Section>
      </main>

      <SiteFooter />
    </div>
  );
}
